import { Component, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AgentLanguageOption, agentLanguageOptions } from './agent-languages';

/**
 * Language picker: type to find, pick to set. The value is the ISO 639-1
 * code; '' means "automatic" (the first option). Typed text is only a
 * search — what was not picked is discarded on blur, so the control never
 * holds anything but '' or a listed code.
 */
@Component({
  selector: 'app-language-select',
  templateUrl: './language-select.component.html',
  styleUrl: './language-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LanguageSelectComponent),
      multi: true,
    },
  ],
})
export class LanguageSelectComponent implements ControlValueAccessor {
  options: AgentLanguageOption[] = [];
  filtered: AgentLanguageOption[] = [];
  /** Text in the field: the picked language's label, or the search. */
  text = '';
  isDisabled = false;

  private code = '';
  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private translate: TranslateService) {
    this.options = agentLanguageOptions(
      this.translate.currentLang || this.translate.defaultLang || 'de'
    );
    this.filtered = this.options;
  }

  writeValue(val: unknown): void {
    this.code = typeof val === 'string' ? val.trim().toLowerCase() : '';
    this.showPicked();
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /** Also nb-autocomplete's display function — an arrow function, because
   *  Nebular calls it without `this`. */
  label = (code: string): string => {
    if (!code) return this.translate.instant('input.defaultLanguage.auto');
    return this.options.find((l) => l.code === code)?.label || code;
  };

  onSearch(text: string) {
    this.text = text;
    const q = text.trim().toLowerCase();
    this.filtered = q
      ? this.options.filter((l) => l.search.includes(q))
      : this.options;
  }

  /** Selecting the text on focus lets the admin type straight over the
   *  current language. */
  onFocus(input: HTMLInputElement) {
    this.filtered = this.options;
    input.select();
  }

  onSelected(code: string) {
    const next = code || '';
    if (next !== this.code) {
      this.code = next;
      this.onChange(next);
    }
    this.showPicked();
  }

  /** Deferred: a click on an option blurs the input first, and its
   *  selection must win over the reset. */
  onBlur() {
    this.onTouched();
    setTimeout(() => this.showPicked(), 200);
  }

  private showPicked() {
    this.text = this.label(this.code);
    this.filtered = this.options;
  }
}
